import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Percent, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { InvoiceDetailModal } from "@/components/modals/InvoiceDetailModal";
import { InvoiceGroupedList } from "@/components/invoices/InvoiceGroupedList";

interface Invoice {
  id: string;
  payment_amount: number;
  payment_due_date: string;
  supplier_name: string;
  supplier_email: string;
  status: string;
  apr_rate: number;
  discount_amount: number | null;
  discounted_payment_amount: number | null;
  email_sent_at: string | null;
  email_error: string | null;
  created_at: string;
  invoice_file_url?: string | null;
  invoice_file_name?: string | null;
}

interface HistoricalTabProps {
  invoices: Invoice[];
}

const aprHistory = [
  { date: "2026-02-09", rate: 12, changedBy: "Pete" },
  { date: "2026-02-05", rate: 15, changedBy: "Pete" },
  { date: "2026-01-28", rate: 10, changedBy: "Pete" },
  { date: "2026-01-15", rate: 8, changedBy: "Pete" },
  { date: "2026-01-02", rate: 12, changedBy: "Pete" },
  { date: "2025-12-20", rate: 14, changedBy: "Pete" },
  { date: "2025-12-05", rate: 10, changedBy: "Pete" },
];

// Seeded deterministic LCG — consistent data across page loads
function makeLcg(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = Math.imul(s, 1664525) + 1013904223;
    return (s >>> 0) / 0xffffffff;
  };
}

// 250 realistic industrial/manufacturing/logistics supplier names
const SUPPLIERS: { name: string; tier: 1 | 2 | 3 }[] = [
  // Tier 1 — large suppliers ($500K–$3M invoices)
  { name: "Apex Industrial Group", tier: 1 },
  { name: "Summit Components Corp", tier: 1 },
  { name: "Keystone Manufacturing", tier: 1 },
  { name: "Atlas Metals & Alloys", tier: 1 },
  { name: "Titan Alloys International", tier: 1 },
  { name: "Pacific Steel Works", tier: 1 },
  { name: "Consolidated Fabrication", tier: 1 },
  { name: "Pioneer Industries LLC", tier: 1 },
  { name: "Frontier Materials Inc", tier: 1 },
  { name: "Vertex Systems Group", tier: 1 },
  { name: "Arrow Components Ltd", tier: 1 },
  { name: "Meridian Manufacturing", tier: 1 },
  { name: "Cascade Industries", tier: 1 },
  { name: "Redstone Metals", tier: 1 },
  { name: "Ironwood Materials", tier: 1 },
  { name: "Broadstone Components", tier: 1 },
  { name: "Nexus Fabrication", tier: 1 },
  { name: "Pinnacle Materials", tier: 1 },
  { name: "Highpoint Industries", tier: 1 },
  { name: "Lakewood Manufacturing", tier: 1 },
  { name: "Greenfield Metals", tier: 1 },
  { name: "Crossroads Supply", tier: 1 },
  { name: "Stonegate Industrial", tier: 1 },
  { name: "Ridgeline Components", tier: 1 },
  { name: "Clearwater Fabrication", tier: 1 },
  // Tier 2 — mid-size suppliers ($100K–$800K invoices)
  { name: "Acme Supplies", tier: 2 },
  { name: "GlobalTech Corp", tier: 2 },
  { name: "Quick Parts Inc", tier: 2 },
  { name: "Steelworks Ltd", tier: 2 },
  { name: "Logistics Pro", tier: 2 },
  { name: "DataSync Solutions", tier: 2 },
  { name: "Pacific Materials", tier: 2 },
  { name: "NorthStar Components", tier: 2 },
  { name: "Vertex Packaging", tier: 2 },
  { name: "Summit Electronics", tier: 2 },
  { name: "Riverstone Materials", tier: 2 },
  { name: "Northern Supply Co.", tier: 2 },
  { name: "Delta Logistics", tier: 2 },
  { name: "Global Fasteners Co.", tier: 2 },
  { name: "Crestview Polymers", tier: 2 },
  { name: "Eastern Seaboard Parts", tier: 2 },
  { name: "Midwest Metals", tier: 2 },
  { name: "Harbor Freight Suppliers", tier: 2 },
  { name: "Coastal Components", tier: 2 },
  { name: "Mountain View Materials", tier: 2 },
  { name: "Sunrise Industrial", tier: 2 },
  { name: "Horizon Manufacturing", tier: 2 },
  { name: "Lakeside Packaging", tier: 2 },
  { name: "Bayshore Alloys", tier: 2 },
  { name: "Westfield Components", tier: 2 },
  { name: "Northgate Supply", tier: 2 },
  { name: "Southland Materials", tier: 2 },
  { name: "Central Valley Parts", tier: 2 },
  { name: "Great Plains Industrial", tier: 2 },
  { name: "Tri-State Components", tier: 2 },
  { name: "Continental Supplies", tier: 2 },
  { name: "Superior Metals", tier: 2 },
  { name: "Alliance Fabrication", tier: 2 },
  { name: "Premier Parts Co", tier: 2 },
  { name: "Reliable Components", tier: 2 },
  { name: "Trusted Suppliers Inc", tier: 2 },
  { name: "Elite Industrial", tier: 2 },
  { name: "ProSource Materials", tier: 2 },
  { name: "TechParts International", tier: 2 },
  { name: "Quantum Components", tier: 2 },
  { name: "Spectrum Manufacturing", tier: 2 },
  { name: "Vector Industries", tier: 2 },
  { name: "Matrix Metals", tier: 2 },
  { name: "Prism Packaging", tier: 2 },
  { name: "Apex Logistics", tier: 2 },
  { name: "Blue Ridge Supply", tier: 2 },
  { name: "Granite State Materials", tier: 2 },
  { name: "Emerald Isle Components", tier: 2 },
  { name: "Copper Canyon Supply", tier: 2 },
  { name: "Silver Creek Metals", tier: 2 },
  { name: "Golden Gate Industrial", tier: 2 },
  { name: "Iron Range Supply", tier: 2 },
  { name: "Cedar Falls Components", tier: 2 },
  { name: "Maple Grove Materials", tier: 2 },
  { name: "Oak Valley Industries", tier: 2 },
  { name: "Pine Ridge Supply", tier: 2 },
  { name: "Willow Creek Parts", tier: 2 },
  { name: "Birch Wood Manufacturing", tier: 2 },
  { name: "Aspen Grove Components", tier: 2 },
  // Tier 3 — smaller suppliers ($30K–$150K invoices)
  { name: "Pioneer Textiles", tier: 3 },
  { name: "Coastal Packaging", tier: 3 },
  { name: "Inland Fabricators", tier: 3 },
  { name: "Valley Stream Parts", tier: 3 },
  { name: "Summit Fasteners", tier: 3 },
  { name: "Crestline Components", tier: 3 },
  { name: "Ridgeway Supply Co", tier: 3 },
  { name: "Hilltop Materials", tier: 3 },
  { name: "Meadow Brook Industrial", tier: 3 },
  { name: "Clearfield Parts", tier: 3 },
  { name: "Westbrook Supply", tier: 3 },
  { name: "Eastwood Components", tier: 3 },
  { name: "Northbrook Metals", tier: 3 },
  { name: "Southridge Materials", tier: 3 },
  { name: "Pinecrest Industrial", tier: 3 },
  { name: "Maplewood Parts", tier: 3 },
  { name: "Elmwood Supply", tier: 3 },
  { name: "Oakdale Components", tier: 3 },
  { name: "Cedarville Packaging", tier: 3 },
  { name: "Walnut Hill Supply", tier: 3 },
  { name: "Chestnut Ridge Parts", tier: 3 },
  { name: "Hawthorn Industrial", tier: 3 },
  { name: "Sycamore Components", tier: 3 },
  { name: "Magnolia Supply", tier: 3 },
  { name: "Laurel Creek Parts", tier: 3 },
  { name: "Holly Springs Industrial", tier: 3 },
  { name: "Juniper Ridge Supply", tier: 3 },
  { name: "Alder Creek Parts", tier: 3 },
  { name: "Hickory Hill Industrial", tier: 3 },
  { name: "Cherry Ridge Components", tier: 3 },
  { name: "Tri-Valley Fasteners", tier: 3 },
  { name: "Five Star Packaging", tier: 3 },
  { name: "Lakeview Metal Works", tier: 3 },
  { name: "Riverside Tooling", tier: 3 },
  { name: "Parkside Stamping", tier: 3 },
  { name: "Highview Precision", tier: 3 },
  { name: "Bridgeport Fabrication", tier: 3 },
  { name: "Millbrook Components", tier: 3 },
  { name: "Centerline Supply", tier: 3 },
  { name: "Fairview Machining", tier: 3 },
  { name: "Glenwood Manufacturing", tier: 3 },
  { name: "Springdale Parts", tier: 3 },
  { name: "Briarwood Supply", tier: 3 },
  { name: "Orchard Hill Industrial", tier: 3 },
  { name: "Windsor Precision Parts", tier: 3 },
  { name: "Concord Metalworks", tier: 3 },
  { name: "Lexington Fabricators", tier: 3 },
  { name: "Franklin Tooling", tier: 3 },
  { name: "Monroe Components", tier: 3 },
  { name: "Jefferson Supply Co", tier: 3 },
  { name: "Lincoln Parts & Supply", tier: 3 },
  { name: "Madison Fabrication", tier: 3 },
  { name: "Harrison Industrial", tier: 3 },
  { name: "Clayton Metalworks", tier: 3 },
  { name: "Shelby Stamping", tier: 3 },
  { name: "Bristol Components", tier: 3 },
  { name: "Chester Packaging", tier: 3 },
  { name: "Dover Industrial", tier: 3 },
  { name: "Lancaster Machining", tier: 3 },
  { name: "Camden Precision", tier: 3 },
  { name: "Trenton Metal Works", tier: 3 },
  { name: "Auburn Components", tier: 3 },
  { name: "Salem Industrial Supply", tier: 3 },
  { name: "Danvers Parts Co", tier: 3 },
  { name: "Gloucester Fabrication", tier: 3 },
  { name: "Rockport Tooling", tier: 3 },
  { name: "Newbury Industrial", tier: 3 },
  { name: "Ipswich Components", tier: 3 },
  { name: "Bradford Supply", tier: 3 },
  { name: "Woburn Machining", tier: 3 },
  { name: "Medford Metalworks", tier: 3 },
  { name: "Waltham Precision", tier: 3 },
  { name: "Quincy Fabricators", tier: 3 },
  { name: "Brockton Components", tier: 3 },
  { name: "Taunton Industrial", tier: 3 },
  { name: "Attleboro Stamping", tier: 3 },
  { name: "Milford Parts Supply", tier: 3 },
  { name: "Fitchburg Tooling", tier: 3 },
  { name: "Leominster Components", tier: 3 },
  { name: "Westfield Industrial", tier: 3 },
  { name: "Agawam Fabrication", tier: 3 },
  { name: "Chicopee Metal Works", tier: 3 },
  { name: "Holyoke Precision", tier: 3 },
  { name: "Northampton Supply", tier: 3 },
  { name: "Greenfield Components", tier: 3 },
  { name: "Montague Machining", tier: 3 },
  { name: "Ware Industrial", tier: 3 },
  { name: "Palmer Fabricators", tier: 3 },
  { name: "Belchertown Parts", tier: 3 },
  { name: "Ludlow Components", tier: 3 },
  { name: "East Longmeadow Supply", tier: 3 },
  { name: "Wilbraham Industrial", tier: 3 },
  { name: "Hampden Tooling", tier: 3 },
  { name: "Monson Precision Parts", tier: 3 },
  { name: "Brimfield Supply Co", tier: 3 },
  { name: "Wales Components", tier: 3 },
  { name: "Holland Industrial", tier: 3 },
  { name: "Blandford Fabrication", tier: 3 },
  { name: "Otis Parts & Supply", tier: 3 },
  { name: "Sandisfield Components", tier: 3 },
  { name: "Tolland Industrial", tier: 3 },
  { name: "Granville Tooling", tier: 3 },
  { name: "Southwick Metalworks", tier: 3 },
  { name: "Suffield Precision", tier: 3 },
  { name: "Enfield Components", tier: 3 },
  { name: "Somers Industrial", tier: 3 },
  { name: "Ellington Supply", tier: 3 },
  { name: "Stafford Fabricators", tier: 3 },
  { name: "Coventry Machining", tier: 3 },
  { name: "Bolton Parts Co", tier: 3 },
  { name: "Columbia Industrial", tier: 3 },
  { name: "Andover Components", tier: 3 },
  { name: "Vernon Tooling", tier: 3 },
  { name: "Tolland Precision", tier: 3 },
  { name: "Willington Supply", tier: 3 },
  { name: "Ashford Components", tier: 3 },
  { name: "Eastford Industrial", tier: 3 },
  { name: "Union Parts Supply", tier: 3 },
  { name: "Pomfret Fabrication", tier: 3 },
  { name: "Woodstock Machining", tier: 3 },
  { name: "Thompson Components", tier: 3 },
  { name: "Putnam Industrial", tier: 3 },
  { name: "Killingly Precision", tier: 3 },
  { name: "Sterling Supply Co", tier: 3 },
  { name: "Webster Components", tier: 3 },
  { name: "Oxford Industrial", tier: 3 },
  { name: "Sutton Tooling", tier: 3 },
  { name: "Douglas Parts & Supply", tier: 3 },
  { name: "Northbridge Fabrication", tier: 3 },
  { name: "Uxbridge Metalworks", tier: 3 },
  { name: "Grafton Precision", tier: 3 },
  { name: "Upton Components", tier: 3 },
  { name: "Mendon Industrial", tier: 3 },
  { name: "Hopedale Supply", tier: 3 },
  { name: "Medway Fabricators", tier: 3 },
  { name: "Millis Tooling", tier: 3 },
  { name: "Wrentham Components", tier: 3 },
  { name: "Norfolk Industrial", tier: 3 },
  { name: "Plainville Supply", tier: 3 },
  { name: "North Attleborough Parts", tier: 3 },
  { name: "Rehoboth Fabrication", tier: 3 },
  { name: "Seekonk Machining", tier: 3 },
  { name: "Swansea Components", tier: 3 },
  { name: "Somerset Industrial", tier: 3 },
  { name: "Berkley Tooling", tier: 3 },
  { name: "Lakeville Precision", tier: 3 },
  { name: "Middleboro Supply", tier: 3 },
  { name: "Bridgewater Components", tier: 3 },
  { name: "Halifax Industrial", tier: 3 },
  { name: "Hanson Fabricators", tier: 3 },
  { name: "Pembroke Tooling", tier: 3 },
  { name: "Duxbury Precision", tier: 3 },
  { name: "Kingston Components", tier: 3 },
  { name: "Plympton Supply", tier: 3 },
  { name: "Carver Industrial", tier: 3 },
  { name: "Wareham Fabrication", tier: 3 },
  { name: "Marion Metalworks", tier: 3 },
  { name: "Rochester Tooling", tier: 3 },
  { name: "Acushnet Components", tier: 3 },
  { name: "Fairhaven Precision", tier: 3 },
];

// Amount ranges by supplier tier (scaled for mid-large industrial buyer ~$300M AP/yr)
const TIER_AMOUNTS = {
  1: { min: 180_000, max: 950_000 },
  2: { min: 25_000, max: 180_000 },
  3: { min: 8_000, max: 45_000 },
};

function supplierEmail(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  return `ap@${slug}.com`;
}

function generateHistoricalInvoices(): Invoice[] {
  const rng = makeLcg(0xdeadbeef);
  const invoices: Invoice[] = [];
  const now = new Date();

  // 48 months of history
  const months: { year: number; month: number; apr: number }[] = [];
  for (let m = 47; m >= 0; m--) {
    const d = new Date(now.getFullYear(), now.getMonth() - m, 1);
    // APR varied over time: started higher, came down
    const apr = m >= 36 ? 14 : m >= 24 ? 12 : m >= 12 ? 10 : 12;
    months.push({ year: d.getFullYear(), month: d.getMonth(), apr });
  }

  let counter = 0;
  months.forEach(({ year, month, apr }, monthIdx) => {
    // Invoice count ramps up over time (program maturity)
    const baseCount = monthIdx < 6 ? 30 : monthIdx < 18 ? 60 : monthIdx < 36 ? 100 : 130;
    const invoiceCount = baseCount + Math.floor(rng() * 20);
    // Accept rate improves over time (supplier trust builds)
    const acceptRate = monthIdx < 6 ? 0.55 : monthIdx < 18 ? 0.68 : monthIdx < 36 ? 0.75 : 0.8;

    for (let i = 0; i < invoiceCount; i++) {
      counter++;
      const supplierIdx = Math.floor(rng() * SUPPLIERS.length);
      const supplier = SUPPLIERS[supplierIdx];
      const { min, max } = TIER_AMOUNTS[supplier.tier];
      const amount = Math.round((min + rng() * (max - min)) / 1000) * 1000;

      const day = Math.min(1 + Math.floor(rng() * 27), 27);
      const hour = 7 + Math.floor(rng() * 10);
      const createdDate = new Date(year, month, day, hour, 0, 0);

      // Due date: 30-60 days after creation
      const termDays = [30, 45, 60][Math.floor(rng() * 3)];
      const dueDate = new Date(createdDate.getTime() + termDays * 86_400_000);

      const isAccelerated = rng() < acceptRate;
      const isCurrentPeriod = monthIdx >= 46;
      const isPending = isCurrentPeriod && rng() < 0.15;
      let status: string;
      if (isPending) {
        status = "pending";
      } else if (isAccelerated) {
        status = monthIdx >= 45 ? "early_payment_scheduled" : "early_payment_completed";
      } else {
        status = "standard_payment";
      }

      const daysEarly = 15 + Math.floor(rng() * 45); // 15–60 days early (avg ~37)
      const discountRate = (apr / 100) * (daysEarly / 365);
      // Set discount_amount on ALL non-pending invoices — gives declined invoices real missed-savings
      const discountAmount = !isPending ? Math.round(amount * discountRate) : null;
      const discountedAmount =
        isAccelerated && !isPending ? Math.round(amount - (discountAmount ?? 0)) : null;

      const emailSentAt =
        status !== "pending" && isAccelerated
          ? new Date(createdDate.getTime() + 86_400_000).toISOString()
          : status === "pending" && rng() < 0.7
            ? new Date(createdDate.getTime() + 86_400_000).toISOString()
            : null;

      invoices.push({
        id: `hist-gen-${counter}`,
        payment_amount: amount,
        payment_due_date: dueDate.toISOString().split("T")[0],
        supplier_name: supplier.name,
        supplier_email: supplierEmail(supplier.name),
        status,
        apr_rate: apr,
        discount_amount: discountAmount,
        discounted_payment_amount: discountedAmount,
        email_sent_at: emailSentAt,
        email_error: null,
        created_at: createdDate.toISOString(),
        invoice_file_url: null,
        invoice_file_name: null,
      });
    }
  });

  return invoices;
}

// Historical invoice data — generated client-side for both dev and production demo
export const historicalInvoiceData = generateHistoricalInvoices();

export function HistoricalTab({ invoices }: HistoricalTabProps) {
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // Merge real invoices with historical data
  const realIds = new Set(invoices.map((i) => i.id));
  const allInvoices = [
    ...invoices,
    ...historicalInvoiceData.filter((h) => !realIds.has(h.id)),
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch {
      return dateString;
    }
  };

  const acceleratedInvoices = allInvoices.filter(
    (i) => i.status === "early_payment_scheduled" || i.status === "early_payment_completed"
  );
  const declinedInvoices = allInvoices.filter((i) => i.status === "standard_payment");
  const allActivityInvoices = allInvoices.filter((i) => i.status !== "pending");

  const totalSaved = acceleratedInvoices.reduce((s, i) => s + (i.discount_amount || 0), 0);
  const totalAccelerated = acceleratedInvoices.reduce(
    (s, i) => s + (i.discounted_payment_amount || i.payment_amount),
    0
  );
  const totalInvoiceAmount = allActivityInvoices.reduce((s, i) => s + i.payment_amount, 0);
  const acceptRate =
    allActivityInvoices.length > 0
      ? (acceleratedInvoices.length / allActivityInvoices.length) * 100
      : 0;

  return (
    <>
      <Card className="glass-card overflow-hidden">
        <div className="p-5 border-b border-border">
          <h2 className="text-base font-display font-semibold text-foreground flex items-center gap-2 tracking-tight">
            <Clock className="w-4 h-4 text-primary" />
            History
          </h2>
          <p className="text-[11px] text-muted-foreground mt-1">
            Track all platform activity over time
          </p>
        </div>

        <div className="grid grid-cols-4 gap-3 p-5 border-b border-border">
          <div className="text-center">
            <p className="text-lg font-bold text-foreground font-display">
              {formatCurrency(totalInvoiceAmount)}
            </p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
              Total Invoiced
            </p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-foreground font-display">
              {formatCurrency(totalSaved)}
            </p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
              Total Saved
            </p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-foreground font-display">
              {formatCurrency(totalAccelerated)}
            </p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
              Accelerated
            </p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-foreground font-display">
              {acceptRate.toFixed(0)}%
            </p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
              Accept Rate
            </p>
          </div>
        </div>

        <Tabs defaultValue="activity" className="p-5">
          <TabsList className="w-full">
            <TabsTrigger
              value="activity"
              className="flex-1 text-xs data-[state=active]:bg-white/[0.08]"
            >
              All Activity
            </TabsTrigger>
            <TabsTrigger value="apr" className="flex-1 text-xs data-[state=active]:bg-white/[0.08]">
              Rate Changes
            </TabsTrigger>
            <TabsTrigger
              value="accepted"
              className="flex-1 text-xs data-[state=active]:bg-white/[0.08]"
            >
              Accelerated
            </TabsTrigger>
            <TabsTrigger
              value="declined"
              className="flex-1 text-xs data-[state=active]:bg-white/[0.08]"
            >
              No Agreement
            </TabsTrigger>
          </TabsList>

          <TabsContent value="activity" className="mt-4">
            <InvoiceGroupedList
              invoices={allActivityInvoices}
              emptyMessage="No activity yet"
              onInvoiceClick={(inv) => setSelectedInvoice(inv as Invoice)}
              maxHeight="300px"
            />
          </TabsContent>

          <TabsContent value="apr" className="mt-4">
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {aprHistory.map((entry, idx) => (
                <div key={idx} className="flex items-center gap-2.5 p-2.5 bg-muted rounded-lg">
                  <Percent className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground">Rate set to {entry.rate}%</p>
                    <p className="text-[10px] text-muted-foreground">
                      {formatDate(entry.date)} by {entry.changedBy}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {idx < aprHistory.length - 1 && (
                      <span
                        className={`text-[10px] font-medium ${entry.rate > aprHistory[idx + 1].rate ? "text-destructive" : "text-success"}`}
                      >
                        {entry.rate > aprHistory[idx + 1].rate ? "↑" : "↓"}
                        {Math.abs(entry.rate - aprHistory[idx + 1].rate)}%
                      </span>
                    )}
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {entry.rate}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="accepted" className="mt-4">
            <InvoiceGroupedList
              invoices={acceleratedInvoices}
              emptyMessage="No accelerated invoices yet"
              onInvoiceClick={(inv) => setSelectedInvoice(inv as Invoice)}
              maxHeight="300px"
            />
          </TabsContent>

          <TabsContent value="declined" className="mt-4">
            <InvoiceGroupedList
              invoices={declinedInvoices}
              emptyMessage="No declined invoices"
              onInvoiceClick={(inv) => setSelectedInvoice(inv as Invoice)}
              maxHeight="300px"
            />
          </TabsContent>
        </Tabs>
      </Card>

      <InvoiceDetailModal
        open={!!selectedInvoice}
        onOpenChange={(open) => !open && setSelectedInvoice(null)}
        invoice={selectedInvoice}
      />
    </>
  );
}
